import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CRow,
    CButton,
    CFormInput,
    CFormSelect,
    CNav,
    CNavItem,
    CNavLink,
    CTable,
    CTableHead,
    CTableRow,
    CTableHeaderCell,
    CTableBody,
    CTableDataCell,
    CSpinner,
    CPagination,
    CPaginationItem,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilReload, cilSearch, cilCloudDownload } from '@coreui/icons';
import { useNavigate } from 'react-router-dom';
import * as DomicilappService from '../../services/DomicilappService';
import { useStore } from '../../contexts/StoreContext';
import { OrderStatus, StoreOrder, StoreOrdersParams } from '../../types/order';
import { showToastTR } from '../../Utils/BaseApp';
import OrderDetailModal from './OrderDetailModal';
import {
    DELIVERY_TYPES,
    DeliveryTypeBadge,
    StatusBadge,
    RiskBadge,
    formatCurrency,
    formatDateTime,
    formatTime,
    timeAgo,
} from './orderUtils';
import notificationSound from '../../assets/sounds/notification.mp3';

export type OrdersView = 'all' | 'active' | 'pickup' | 'history';

const VIEWS: { key: OrdersView; label: string; to: string; description: string }[] = [
    { key: 'all', label: 'Todas', to: '/orders', description: 'Todas las órdenes recibidas por la tienda' },
    { key: 'active', label: 'Por atender', to: '/orders/active', description: 'Órdenes en curso que la tienda debe preparar o despachar' },
    { key: 'pickup', label: 'Recoger en tienda', to: '/orders/pickup', description: 'Pedidos que el cliente recoge en el mostrador' },
    { key: 'history', label: 'Historial', to: '/orders/history', description: 'Órdenes finalizadas' },
];

const PER_PAGE = 20;
const REFRESH_MS = 30000;

const baseParamsByView = (view: OrdersView): StoreOrdersParams => {
    switch (view) {
        case 'active':
            return { group: 'active' };
        case 'pickup':
            return { group: 'active', delivery_type_id: DELIVERY_TYPES.PICKUP };
        case 'history':
            return { group: 'history' };
        default:
            return {};
    }
};

export const OrdersPage: React.FC<{ view: OrdersView }> = ({ view }) => {
    const navigate = useNavigate();
    const { storeId, storeDetails } = useStore();

    const [orders, setOrders] = useState<StoreOrder[]>([]);
    const [statuses, setStatuses] = useState<OrderStatus[]>([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

    // Filtros
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusId, setStatusId] = useState('');
    const [deliveryTypeId, setDeliveryTypeId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Para avisar de órdenes nuevas entre recargas automáticas
    const lastSeenOrderId = useRef<number>(0);

    const autoRefresh = view === 'active' || view === 'pickup' || view === 'all';
    const finalStatusId = statuses.length ? Math.max(...statuses.map((s) => s.id)) : 0;
    const currentView = VIEWS.find((v) => v.key === view)!;

    useEffect(() => {
        DomicilappService.getOrderStatuses()
            .then((response) => setStatuses(response?.data ?? []))
            .catch(() => setStatuses([]));
    }, []);

    // Al cambiar de vista o tienda se limpian filtros y página
    useEffect(() => {
        setPage(1);
        setStatusId('');
        setDeliveryTypeId('');
        setSearchInput('');
        setSearch('');
        setDateFrom('');
        setDateTo('');
        lastSeenOrderId.current = 0;
    }, [view, storeId]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timeout);
    }, [searchInput]);

    const loadOrders = useCallback(
        (silent = false) => {
            if (!storeId) return;
            if (!silent) setLoading(true);

            const params: StoreOrdersParams = { ...baseParamsByView(view), page, per_page: PER_PAGE };
            if (statusId) params.status_id = statusId;
            if (deliveryTypeId) params.delivery_type_id = deliveryTypeId;
            if (search) params.search = search;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            DomicilappService.getOrdersByStore(storeId, params)
                .then((response) => {
                    const items = response?.data?.items ?? [];
                    const newest = items.reduce((max, o) => Math.max(max, o.id), 0);
                    if (silent && lastSeenOrderId.current && newest > lastSeenOrderId.current) {
                        showToastTR(`¡Nueva orden #${newest}!`, 5000, 'info');
                        new Audio(notificationSound).play().catch(() => undefined);
                    }
                    lastSeenOrderId.current = Math.max(lastSeenOrderId.current, newest);

                    setOrders(items);
                    setLastPage(response?.data?.last_page ?? 1);
                    setTotal(response?.data?.total ?? 0);
                    setLastUpdate(new Date());
                })
                .catch((error) => {
                    console.error('Error al obtener órdenes:', error);
                    if (!silent) setOrders([]);
                })
                .finally(() => {
                    if (!silent) setLoading(false);
                });
        },
        [storeId, view, page, statusId, deliveryTypeId, search, dateFrom, dateTo],
    );

    useEffect(() => {
        setOrders([]);
        loadOrders();
    }, [loadOrders]);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(() => loadOrders(true), REFRESH_MS);
        return () => clearInterval(interval);
    }, [autoRefresh, loadOrders]);

    const nextStatusName = (order: StoreOrder) => statuses.find((s) => s.id > order.status_id)?.name;

    const downloadCSV = () => {
        const headers = ['Orden', 'Fecha', 'Cliente', 'Teléfono', 'Modalidad', 'Estado', 'Items', 'Productos', 'Domicilio', 'Empaque', 'Total'];
        const rows = orders.map((o) => [
            o.id,
            o.created_at,
            o.user_full_name,
            o.user_phone_number ?? '',
            o.delivery_type_name,
            o.status_name,
            o.items_quantity,
            o.price,
            o.delivery_price,
            o.packaging_fee ?? 0,
            o.total_price,
        ]);
        const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
        link.download = `ordenes_${view}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const showDateFilters = view === 'all' || view === 'history';
    const showStatusFilter = view !== 'history';
    const showDeliveryTypeFilter = view !== 'pickup';

    const pages = Array.from({ length: lastPage }, (_, i) => i + 1).filter(
        (p) => p === 1 || p === lastPage || Math.abs(p - page) <= 2,
    );

    return (
        <div className="orders-page">
            {/* CABECERA */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                <div>
                    <h3 className="fw-bold text-body mb-1">Órdenes</h3>
                    <p className="text-body-secondary small mb-0">
                        {currentView.description}
                        {storeDetails?.name ? ` · ${storeDetails.name}` : ''}
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {lastUpdate && (
                        <small className="text-body-secondary">
                            {autoRefresh ? 'Actualización automática · ' : ''}
                            {formatTime(lastUpdate.toISOString())}
                        </small>
                    )}
                    <CButton
                        color="light"
                        size="sm"
                        className="border text-body bg-body-tertiary d-flex align-items-center gap-1 px-3"
                        onClick={() => loadOrders()}
                        disabled={loading}
                    >
                        <CIcon icon={cilReload} size="sm" />
                        <span>Actualizar</span>
                    </CButton>
                    <CButton
                        color="light"
                        size="sm"
                        className="border text-body bg-body-tertiary d-flex align-items-center gap-1 px-3"
                        onClick={downloadCSV}
                        disabled={orders.length === 0}
                    >
                        <CIcon icon={cilCloudDownload} size="sm" />
                        <span>Exportar</span>
                    </CButton>
                </div>
            </div>

            <CCard className="border-0 shadow-sm rounded-3">
                <CCardHeader className="bg-body border-bottom border-secondary-subtle pt-3 pb-0">
                    <CNav variant="tabs" className="card-header-tabs border-0">
                        {VIEWS.map((v) => (
                            <CNavItem key={v.key}>
                                <CNavLink
                                    active={v.key === view}
                                    onClick={() => navigate(v.to)}
                                    className={`px-3 py-2 fw-medium border-0 bg-transparent ${v.key === view
                                        ? 'text-body fw-bold border-bottom border-2 border-danger'
                                        : 'text-body-secondary'
                                        }`}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {v.label}
                                </CNavLink>
                            </CNavItem>
                        ))}
                    </CNav>
                </CCardHeader>

                <CCardBody className="p-4">
                    {/* FILTROS */}
                    <CRow className="g-3 mb-4">
                        <CCol md={showDateFilters ? 4 : 6}>
                            <div className="position-relative">
                                <CFormInput
                                    placeholder="Buscar por # orden, cliente o teléfono..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="ps-5"
                                />
                                <CIcon
                                    icon={cilSearch}
                                    className="position-absolute text-body-secondary"
                                    style={{ left: '14px', top: '50%', transform: 'translateY(-50%)' }}
                                />
                            </div>
                        </CCol>
                        {showStatusFilter && (
                            <CCol md={showDateFilters ? 2 : 3}>
                                <CFormSelect value={statusId} onChange={(e) => { setStatusId(e.target.value); setPage(1); }}>
                                    <option value="">Todos los estados</option>
                                    {statuses
                                        .filter((s) => view === 'all' || s.id !== finalStatusId)
                                        .map((s) => (
                                            <option key={s.id} value={s.id} style={{ textTransform: 'capitalize' }}>
                                                {s.name.charAt(0).toUpperCase() + s.name.slice(1)}
                                            </option>
                                        ))}
                                </CFormSelect>
                            </CCol>
                        )}
                        {showDeliveryTypeFilter && (
                            <CCol md={showDateFilters ? 2 : 3}>
                                <CFormSelect value={deliveryTypeId} onChange={(e) => { setDeliveryTypeId(e.target.value); setPage(1); }}>
                                    <option value="">Todas las modalidades</option>
                                    <option value={DELIVERY_TYPES.DELIVERY}>Domicilio</option>
                                    <option value={DELIVERY_TYPES.PICKUP}>Recoger en tienda</option>
                                    <option value={DELIVERY_TYPES.SCHEDULED}>Programado</option>
                                </CFormSelect>
                            </CCol>
                        )}
                        {showDateFilters && (
                            <>
                                <CCol xs={6} md={2}>
                                    <CFormInput type="date" title="Desde" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                                </CCol>
                                <CCol xs={6} md={2}>
                                    <CFormInput type="date" title="Hasta" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
                                </CCol>
                            </>
                        )}
                    </CRow>

                    {/* TABLA */}
                    <CTable responsive align="middle" hover className="border-top mb-0">
                        <CTableHead>
                            <CTableRow>
                                <CTableHeaderCell>Orden</CTableHeaderCell>
                                <CTableHeaderCell>Cliente</CTableHeaderCell>
                                <CTableHeaderCell>Modalidad</CTableHeaderCell>
                                <CTableHeaderCell>Estado</CTableHeaderCell>
                                <CTableHeaderCell>Entrega</CTableHeaderCell>
                                <CTableHeaderCell className="text-center">Items</CTableHeaderCell>
                                <CTableHeaderCell>Total</CTableHeaderCell>
                                <CTableHeaderCell className="text-end">Acciones</CTableHeaderCell>
                            </CTableRow>
                        </CTableHead>
                        <CTableBody>
                            {loading ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={8} className="text-center py-5">
                                        <CSpinner color="primary" size="sm" className="me-2" />
                                        <span className="text-body-secondary">Cargando órdenes de la tienda...</span>
                                    </CTableDataCell>
                                </CTableRow>
                            ) : orders.length === 0 ? (
                                <CTableRow>
                                    <CTableDataCell colSpan={8} className="text-center py-5 text-body-secondary">
                                        {storeId ? 'No hay órdenes para mostrar con estos filtros.' : 'Selecciona una tienda para ver sus órdenes.'}
                                    </CTableDataCell>
                                </CTableRow>
                            ) : (
                                orders.map((order) => {
                                    const nextName = nextStatusName(order);
                                    return (
                                        <CTableRow key={order.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedOrderId(order.id)}>
                                            <CTableDataCell>
                                                <div className="fw-bold text-body">#{order.id}</div>
                                                <small className="text-body-secondary" title={formatDateTime(order.created_at)}>
                                                    {view === 'history' ? formatDateTime(order.created_at) : timeAgo(order.created_at)}
                                                </small>
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                <div className="fw-semibold text-body">{order.user_full_name}</div>
                                                <small className="text-body-secondary">{order.user_phone_number}</small>
                                                {/* Riesgo medio o alto: los motivos y puntos se ven al pasar el mouse */}
                                                {order.risk_level && order.risk_level !== 'low' && (
                                                    <div className="mt-1">
                                                        <RiskBadge level={order.risk_level} score={order.risk_score} reasons={order.risk_reasons} />
                                                    </div>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                <DeliveryTypeBadge typeId={order.delivery_type_id} name={order.delivery_type_name} />
                                                {order.delivery_date && (
                                                    <small className="d-block text-body-secondary mt-1">{formatDateTime(order.delivery_date)}</small>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                <StatusBadge name={order.status_name} color={order.status_color} />
                                            </CTableDataCell>
                                            <CTableDataCell>
                                                {Number(order.delivery_type_id) === DELIVERY_TYPES.PICKUP ? (
                                                    <small className="text-body-secondary">En mostrador</small>
                                                ) : order.courier_full_name ? (
                                                    <>
                                                        <div className="small text-body">{order.courier_full_name}</div>
                                                        <small className="text-body-secondary" style={{ textTransform: 'capitalize' }}>{order.delivery_status_name}</small>
                                                    </>
                                                ) : (
                                                    <small className="text-warning">Buscando domiciliario</small>
                                                )}
                                            </CTableDataCell>
                                            <CTableDataCell className="text-center text-body">{order.items_quantity}</CTableDataCell>
                                            <CTableDataCell className="fw-bold text-body text-nowrap">{formatCurrency(order.total_price)}</CTableDataCell>
                                            <CTableDataCell className="text-end text-nowrap">
                                                {nextName && Number(order.status_id) !== finalStatusId && (
                                                    <CButton
                                                        size="sm"
                                                        className="me-2 text-white"
                                                        style={{ backgroundColor: '#FE2445', borderColor: '#FE2445' }}
                                                        title={`Pasar a "${nextName}"`}
                                                        onClick={(e) => { e.stopPropagation(); setSelectedOrderId(order.id); }}
                                                    >
                                                        Gestionar
                                                    </CButton>
                                                )}
                                                <CButton
                                                    color="light"
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); setSelectedOrderId(order.id); }}
                                                >
                                                    Ver
                                                </CButton>
                                            </CTableDataCell>
                                        </CTableRow>
                                    );
                                })
                            )}
                        </CTableBody>
                    </CTable>

                    {/* PAGINACIÓN */}
                    {total > 0 && (
                        <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
                            <small className="text-body-secondary">{total} órdenes</small>
                            {lastPage > 1 && (
                                <CPagination className="mb-0" size="sm">
                                    <CPaginationItem disabled={page === 1} onClick={() => setPage(page - 1)} style={{ cursor: 'pointer' }}>
                                        Anterior
                                    </CPaginationItem>
                                    {pages.map((p, i) => (
                                        <React.Fragment key={p}>
                                            {i > 0 && p - pages[i - 1] > 1 && <CPaginationItem disabled>…</CPaginationItem>}
                                            <CPaginationItem active={p === page} onClick={() => setPage(p)} style={{ cursor: 'pointer' }}>
                                                {p}
                                            </CPaginationItem>
                                        </React.Fragment>
                                    ))}
                                    <CPaginationItem disabled={page === lastPage} onClick={() => setPage(page + 1)} style={{ cursor: 'pointer' }}>
                                        Siguiente
                                    </CPaginationItem>
                                </CPagination>
                            )}
                        </div>
                    )}
                </CCardBody>
            </CCard>

            <OrderDetailModal
                storeId={storeId}
                orderId={selectedOrderId}
                onClose={() => setSelectedOrderId(null)}
                onStatusChanged={() => loadOrders(true)}
            />
        </div>
    );
};

export default OrdersPage;
